/*
 * Phone field with a mandatory country code (snippets/phone-field.liquid).
 *
 * Why it exists: `phone` used to be a free-text input, so numbers arrived without a
 * country code. That costs twice — an operator has to guess where to call, and Meta
 * matches a national number against almost nobody (the Conversions API hashes digits
 * only, so "1701234567" and "+491701234567" are different people to it).
 *
 * The control is a custom combobox rather than a <select>, for two reasons: a native
 * select can only ever show the selected option's own text (so the closed control could
 * not be a compact flag + dial code), and 245 options with no way to search means
 * scrolling past forty countries to reach Germany on a phone.
 *
 * The visible parts carry no name; a hidden input named `phone` holds the assembled
 * value, which is what every form already submits. Nothing else had to change.
 *
 * Dial codes: assets/phone-countries.js (GENERATED from libphonenumber-js — see
 * theme/migration/_gen-phone-countries.mjs).
 */
(function () {
  var PRIORITY = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PL', 'SE', 'GB', 'US', 'AE', 'AU'];

  // Countries whose national numbers KEEP the leading zero. Everywhere else it is a
  // trunk prefix that must be dropped before the dial code: "+49 0170…" is not a number.
  // Italy is the well-known exception — its landline zero is part of the number itself.
  var KEEPS_LEADING_ZERO = { IT: true };

  function flag(iso) {
    try {
      return String.fromCodePoint(
        0x1f1e6 + iso.charCodeAt(0) - 65,
        0x1f1e6 + iso.charCodeAt(1) - 65
      );
    } catch (e) {
      return '';
    }
  }

  /* Region from the browser locale ("de-DE" -> "DE"), our only hint when the form has no
     country select. Wrong beats empty: a visible flag invites a correction, an empty
     control invites a number with no code at all. */
  function localeRegion() {
    try {
      var tags = [navigator.language].concat(navigator.languages || []);
      for (var i = 0; i < tags.length; i++) {
        var m = /-([A-Z]{2})$/.exec(String(tags[i] || ''));
        if (m) return m[1];
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  function init(root) {
    if (root.dataset.phoneReady === '1') return;
    var list = window.BQ_PHONE_COUNTRIES;
    if (!list || !list.length) return; // data asset missing — leave the inputs alone
    root.dataset.phoneReady = '1';

    var toggle = root.querySelector('[data-phone-toggle]');
    var flagEl = root.querySelector('[data-phone-flag]');
    var dialEl = root.querySelector('[data-phone-dial]');
    var number = root.querySelector('[data-phone-number]');
    var panel = root.querySelector('[data-phone-panel]');
    var search = root.querySelector('[data-phone-search]');
    var listEl = root.querySelector('[data-phone-list]');
    var hidden = root.querySelector('[data-phone-value]');
    var hiddenCountry = root.querySelector('[data-phone-country]');
    if (!toggle || !number || !panel || !listEl || !hidden) return;

    var byIso = {};
    var byName = {};
    for (var i = 0; i < list.length; i++) {
      byIso[list[i][0]] = list[i];
      byName[String(list[i][2]).toLowerCase()] = list[i];
    }

    var current = null;   // the selected row [iso, dial, name]
    var options = [];     // rendered <li> elements, in display order
    var activeIndex = -1; // keyboard highlight

    // Priority markets first (continental EU, then the English-speaking ones), then
    // everything else alphabetically. Filtering keeps the same order.
    function ordered() {
      var head = [];
      for (var p = 0; p < PRIORITY.length; p++) if (byIso[PRIORITY[p]]) head.push(byIso[PRIORITY[p]]);
      var rest = list.filter(function (row) { return PRIORITY.indexOf(row[0]) === -1; });
      return head.concat(rest);
    }

    function render(filter) {
      var needle = String(filter || '').trim().toLowerCase();
      listEl.textContent = '';
      options = [];
      activeIndex = -1;

      var rows = ordered().filter(function (row) {
        if (!needle) return true;
        // Match the name, the ISO code, or the dial code with or without its "+".
        return (
          row[2].toLowerCase().indexOf(needle) !== -1 ||
          row[0].toLowerCase().indexOf(needle) === 0 ||
          ('+' + row[1]).indexOf(needle) === 0 ||
          row[1].indexOf(needle.replace(/^\+/, '')) === 0
        );
      });

      if (!rows.length) {
        var empty = document.createElement('li');
        empty.className = 'phone-field__empty';
        empty.textContent = '—';
        listEl.appendChild(empty);
        return;
      }

      for (var r = 0; r < rows.length; r++) {
        var row = rows[r];
        var li = document.createElement('li');
        li.className = 'phone-field__option';
        li.setAttribute('role', 'option');
        li.dataset.iso = row[0];
        li.setAttribute('aria-selected', current && current[0] === row[0] ? 'true' : 'false');

        var f = document.createElement('span');
        f.className = 'phone-field__option-flag';
        f.textContent = flag(row[0]);
        var n = document.createElement('span');
        n.className = 'phone-field__option-name';
        n.textContent = row[2];
        var d = document.createElement('span');
        d.className = 'phone-field__option-dial';
        d.textContent = '+' + row[1];

        li.appendChild(f);
        li.appendChild(n);
        li.appendChild(d);
        listEl.appendChild(li);
        options.push(li);
      }
    }

    function setActive(index) {
      if (!options.length) return;
      if (activeIndex >= 0 && options[activeIndex]) options[activeIndex].classList.remove('is-active');
      activeIndex = Math.max(0, Math.min(index, options.length - 1));
      var el = options[activeIndex];
      el.classList.add('is-active');
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    // Assemble +E.164 into the hidden input the form actually submits. An empty number
    // means an empty `phone` — the dial code alone is not a phone number, and where the
    // field is optional it must stay genuinely optional.
    function sync() {
      var raw = String(number.value || '').replace(/\D/g, '');
      var iso = current ? current[0] : '';
      if (raw && !KEEPS_LEADING_ZERO[iso]) raw = raw.replace(/^0+/, '');
      hidden.value = raw && current ? '+' + current[1] + raw : '';
      if (hiddenCountry) hiddenCountry.value = raw && current ? iso : '';
    }

    function select(iso, keepFocus) {
      var row = byIso[iso];
      if (!row) return false;
      current = row;
      if (flagEl) flagEl.textContent = flag(row[0]);
      if (dialEl) dialEl.textContent = '+' + row[1];
      sync();
      if (keepFocus && number.focus) number.focus();
      return true;
    }

    // The list is built here, on open, and never at init: 245 countries are ~1,000 nodes per
    // field, which every page with a form carried for a panel most visitors never open
    // (Lighthouse DOM size, docs/plans/lighthouse-2026-10.md).
    function open() {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      if (search) search.value = '';
      render('');
      if (search) {
        try { search.focus({ preventScroll: true }); } catch (e) { search.focus(); }
      }
      var selectedIndex = 0;
      for (var i2 = 0; i2 < options.length; i2++) {
        if (current && options[i2].dataset.iso === current[0]) { selectedIndex = i2; break; }
      }
      setActive(selectedIndex);
    }

    function close() {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (panel.hidden) open(); else close();
    });

    listEl.addEventListener('click', function (e) {
      var li = e.target.closest && e.target.closest('.phone-field__option');
      if (!li) return;
      select(li.dataset.iso, true);
      close();
    });

    if (search) {
      search.addEventListener('input', function () {
        render(search.value);
        setActive(0);
      });
      search.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
        else if (e.key === 'Enter') {
          e.preventDefault();
          if (options[activeIndex]) { select(options[activeIndex].dataset.iso, true); close(); }
        } else if (e.key === 'Escape') { e.preventDefault(); close(); toggle.focus(); }
      });
    }

    // Click anywhere else closes it. Bound on the document because the panel is not a
    // <dialog> and gets no light-dismiss of its own.
    document.addEventListener('click', function (e) {
      if (panel.hidden) return;
      if (!root.contains(e.target)) close();
    });

    number.addEventListener('input', sync);
    number.addEventListener('change', sync);

    // Initial country: what the form's own country select says, else the browser locale.
    var form = root.closest('form');
    var countrySelect = form && form.querySelector('select[name="country"]');
    var userPicked = false;
    listEl.addEventListener('click', function () { userPicked = true; });

    function fromCountrySelect() {
      if (!countrySelect || !countrySelect.value) return null;
      var row = byName[String(countrySelect.value).trim().toLowerCase()];
      return row ? row[0] : null;
    }

    if (!select(fromCountrySelect())) {
      if (!select(localeRegion())) select(PRIORITY[0]);
    }

    // Follow the country select until the visitor overrides the code by hand — after
    // that it is their choice, and a later country change must not undo it.
    if (countrySelect) {
      countrySelect.addEventListener('change', function () {
        if (userPicked) return;
        select(fromCountrySelect());
      });
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-phone-field]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  window.bqInitPhoneFields = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
